import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { catchError, forkJoin, from, map, of, switchMap, take } from 'rxjs';
import { ActionLogArea, ActionLogSearchRequest } from '../shared/models/action-log.model';
import { ActionLogService } from '../shared/services/action-log/action-log.service';
import { ApplicationsService } from '../shared/services/applications/applications.service';
import { ClientService } from '../shared/services/client/client.service';
import { FactFindService } from '../shared/services/fact-find/fact-find.service';
import { MarketingPreferencesService } from '../shared/services/marketingpreferences/marketing-preferences.service';
import { ClientStore } from '../shared/stores/client/client.store';
import { awaitClientReady } from '../shared/utils/client/client.util';

/**
 * Preloads dashboard data for the dashboard route.
 * @returns An observable that resolves to true if the data was successfully preloaded, or false otherwise.
 */
export const dashboardResolver: ResolveFn<boolean> = () => {
  const clientStore = inject(ClientStore);
  const applicationsService = inject(ApplicationsService);
  const clientService = inject(ClientService);
  const actionLogService = inject(ActionLogService);
  const marketingPreferencesService = inject(MarketingPreferencesService);
  const factFindService = inject(FactFindService);
  const router = inject(Router);

  return from(awaitClientReady(clientStore)).pipe(
    switchMap((client) => {
      if (!client) {
        return of(false);
      }

      const personal$ = clientService.getPersonalDetails(client.clientId).pipe(take(1));
      const history$ = clientService.getAddressHistory(client.clientId).pipe(take(1));
      const marketingPreferences$ = marketingPreferencesService.get(client.clientId).pipe(take(1));
      const apps$ = applicationsService.get(client.clientId).pipe(take(1));
      const clientSectionTypes$ = factFindService.getSectionTypes().pipe(take(1));
      const actionLog$ = actionLogService
        .search({
          organisationId: client.brokerageId,
          externalReference1: '',
          externalReference2: client.clientId.toString(),
          area: ActionLogArea.CaseUserAction,
        } as ActionLogSearchRequest)
        .pipe(take(1));

      return forkJoin({
        apps: apps$,
        personal: personal$,
        history: history$,
        actionLog: actionLog$,
        marketingPreferences: marketingPreferences$,
        sectionTypes: clientSectionTypes$,
      }).pipe(
        map(() => true),
        catchError((error) => {
          console.error('Dashboard data preload failed', error);
          router.navigate(['/error'], {
            queryParams: { message: 'Failed to load dashboard data. Please try again later.' },
          });
          return of(false);
        })
      );
    })
  );
};
